import styled from "~ui/styled"
import { NavLink, Outlet, useLocation } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useTheme } from "~ui/styled"
import { useStores } from "~models"
import { CenterFlex, IconButton, IconFont } from "~components/El"
import HomeHeader from "~components/home-header"
import { Dropdown, LogoHeader } from "~components/chat-list/dropdown"
import useNavigation from "~hooks/useNavigation"

const Shell = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  min-height: 48px;
  height: 48px;
  overflow: visible;
  padding: 0 10px;
  background: ${({ theme }) => theme.colors.blockBackground};
  /* border-bottom: 1px solid ${({ theme }) => theme.colors.border}; */
`

const Title = styled(CenterFlex)`
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

/** 传统底部 Tab：横向均分，上图标下文字 */
const TabBar = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex-shrink: 0;
  width: 100%;
  min-height: 56px;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: ${({ theme }) => theme.colors.blockBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
`

const TabItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 0;
  padding: 6px 4px 8px;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  color: ${({ theme }) => theme.colors.secondaryText};
  transition: color 0.15s ease;

  &.active {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    opacity: 0.75;
  }
`

const TabIconWrap = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
`

const TabLabel = styled.span`
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
`

const TabBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -10px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.danger};
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  font-weight: 600;
  box-sizing: border-box;
`

const TAB_META: Record<
  string,
  { label: string; icon: string; title?: string; showHomeHeader?: boolean; devOnly?: boolean }
> = {
  "/chats": { label: "聊天", icon: "lt", showHomeHeader: true },
  "/contacts": { label: "好友", icon: "hy", title: "好友" },
  "/groups": { label: "群聊", icon: "txl", showHomeHeader: true },
  "/me": { label: "我的", icon: "wd", title: "我的" },
  // "/debug": { label: "调试", icon: "wxts", title: "调试", devOnly: true },
}

function TabLayoutBase() {
  const theme = useTheme()
  const location = useLocation()
  const navigation = useNavigation()
  const { messageStore, friendStore } = useStores()
  const meta = TAB_META[location.pathname] || { label: "", icon: "lt" }
  const unread = messageStore.unread
  const friendCount = friendStore.friendsCount

  const badgeFor = (path: string) => {
    if (path === "/chats" && unread > 0) return unread > 100 ? "99+" : String(unread)
    if (path === "/contacts" && friendCount > 0) return String(friendCount)
    return null
  }

  const tabs = Object.entries(TAB_META).filter(([, tab]) => !tab.devOnly || __DEV__)

  return (
    <Shell>
      <Header>
        <div style={{ minWidth: 80 }}>
          {meta.showHomeHeader ? <LogoHeader /> : null}
        </div>
        <Title>{meta.showHomeHeader ? <HomeHeader /> : meta.title}</Title>
        <div style={{ minWidth: 80, display: "flex", justifyContent: "flex-end" }}>
          {location.pathname === "/chats" || location.pathname === "/groups" ? <Dropdown /> : null}
          {location.pathname === "/contacts" ? (
            <IconButton
              name="tjhy"
              size={36}
              iconSize={16}
              iconColor={theme.colors.text}
              onClick={() => navigation.push("AddContact")}
            />
          ) : null}
        </div>
      </Header>
      <Content>
        <Outlet />
      </Content>
      <TabBar aria-label="主导航">
        {tabs.map(([path, tab]) => {
          const badge = badgeFor(path)
          return (
            <TabItem key={path} to={path}>
              {({ isActive }) => (
                <>
                  <TabIconWrap>
                    <IconFont
                      name={tab.icon}
                      color={isActive ? theme.colors.primary : theme.colors.secondaryText}
                      size={22}
                    />
                    {badge ? <TabBadge>{badge}</TabBadge> : null}
                  </TabIconWrap>
                  <TabLabel style={{ fontWeight: isActive ? 600 : 400, color: "inherit" }}>
                    {tab.label}
                  </TabLabel>
                </>
              )}
            </TabItem>
          )
        })}
      </TabBar>
    </Shell>
  )
}

export default observer(TabLayoutBase)
